"use client";
import { useSearchParams } from "next/navigation";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useDebounce from "@/hooks/useDebounce";
import {
  buildSearchHref,
  type ISearchCategoryOption,
} from "./advanced-search-helpers";
import AdvancedSearchCategoryMenu from "./AdvancedSearchCategoryMenu";
import AdvancedSearchSuggestions from "./AdvancedSearchSuggestions";

interface Props {
  categories: ISearchCategoryOption[];
}

interface FieldsProps extends Props {
  initialQuery: string;
  initialCategory: string;
}

const AdvancedSearchFields: React.FC<FieldsProps> = ({
  categories,
  initialQuery,
  initialCategory,
}) => {
  const t = useTranslation();
  const router = useRouter();
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState(initialCategory);
  const [open, setOpen] = useState(false);
  const debouncedTerm = useDebounce(query.trim(), 300);
  const showSuggestions = open && !!query.trim() && !!debouncedTerm;

  // Closing + keyboard navigation for the open panel, as native listeners on the
  // widget root (one place, and no handlers on non-interactive wrappers).
  // Closing on pointerdown-outside, not on blur: iOS Safari doesn't focus links
  // on tap, so a blur-based close would unmount a result before its click lands.
  useEffect(() => {
    const root = rootRef.current;
    if (!open || !root) return;
    const input = inputRef.current;
    const getResultLinks = () =>
      Array.from(
        root.querySelectorAll<HTMLAnchorElement>(".advanced-search__result"),
      );

    const handlePointerDown = (event: PointerEvent) => {
      if (!root.contains(event.target as Node)) setOpen(false);
    };
    // Keyboard focus (Tab) leaving the whole widget.
    const handleFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (next && !root.contains(next)) setOpen(false);
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        input?.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const links = getResultLinks();
      const index = links.indexOf(document.activeElement as HTMLAnchorElement);
      // Arrows only move between the input and the results, never the category menu.
      if (event.target !== input && index === -1) return;
      if (!links.length) return;
      event.preventDefault();
      const next = index + (event.key === "ArrowDown" ? 1 : -1);
      if (next < 0) input?.focus();
      else links[Math.min(next, links.length - 1)]?.focus();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    root.addEventListener("focusout", handleFocusOut);
    root.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      root.removeEventListener("focusout", handleFocusOut);
      root.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // Native form submit covers both the button click and Enter (and skips Enter
  // while an IME composition is open), so there is no keydown handling here.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!query.trim()) {
      inputRef.current?.focus();
      return;
    }
    setOpen(false);
    // Dismiss the on-screen keyboard on mobile before the route changes.
    inputRef.current?.blur();
    router.push(buildSearchHref(query, category));
  };

  return (
    <div ref={rootRef} className="advanced-search">
      <form
        role="search"
        className="advanced-search__bar"
        onSubmit={handleSubmit}
      >
        <input
          ref={inputRef}
          type="search"
          className="advanced-search__input"
          placeholder={t.advanced_search_placeholder}
          aria-label={t.search}
          aria-controls={showSuggestions ? panelId : undefined}
          autoComplete="off"
          enterKeyHint="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        <AdvancedSearchCategoryMenu
          categories={categories}
          value={category}
          onOpen={() => setOpen(false)}
          onChange={(slug) => {
            setCategory(slug);
            // Show the re-scoped suggestions straight away.
            if (query.trim()) setOpen(true);
          }}
        />
        <button
          type="submit"
          className="advanced-search__submit"
          aria-label={t.search}
        >
          <svg
            aria-hidden="true"
            focusable="false"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M10.25 3C6.25482 3 3 6.25482 3 10.25C3 14.2452 6.25482 17.5 10.25 17.5C11.9782 17.5 13.5669 16.8895 14.8145 15.875L19.7197 20.7803C19.7888 20.8523 19.8716 20.9097 19.9632 20.9493C20.0548 20.9889 20.1534 21.0098 20.2532 21.0108C20.3529 21.0118 20.4519 20.9929 20.5443 20.9552C20.6367 20.9175 20.7206 20.8617 20.7912 20.7912C20.8617 20.7206 20.9175 20.6367 20.9552 20.5443C20.9929 20.4519 21.0118 20.3529 21.0108 20.2532C21.0098 20.1534 20.9889 20.0548 20.9493 19.9632C20.9097 19.8716 20.8523 19.7888 20.7803 19.7197L15.875 14.8145C16.8895 13.5669 17.5 11.9782 17.5 10.25C17.5 6.25482 14.2452 3 10.25 3ZM10.25 4.5C13.4345 4.5 16 7.06548 16 10.25C16 11.8013 15.3881 13.2029 14.3955 14.2354C14.3343 14.2803 14.2803 14.3343 14.2354 14.3955C13.2029 15.3881 11.8013 16 10.25 16C7.06548 16 4.5 13.4345 4.5 10.25C4.5 7.06548 7.06548 4.5 10.25 4.5Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </form>
      {showSuggestions && (
        <AdvancedSearchSuggestions
          id={panelId}
          term={debouncedTerm}
          category={category}
          onSelect={() => setOpen(false)}
        />
      )}
    </div>
  );
};

const AdvancedSearchForm: React.FC<Props> = ({ categories }) => {
  const searchParams = useSearchParams();
  const q = searchParams?.get("q") ?? "";
  const categoryParam = searchParams?.get("category_id") ?? "";
  // A category_id that isn't a top-level option (e.g. a subcategory slug) would
  // leave the menu showing "All categories" while state holds another value.
  const initialCategory = categories.some(({ slug }) => slug === categoryParam)
    ? categoryParam
    : "";
  // Remount on URL change so the fields reflect the search being shown when the
  // header persists across client navigations.
  return (
    <AdvancedSearchFields
      key={`${q}|${initialCategory}`}
      categories={categories}
      initialQuery={q}
      initialCategory={initialCategory}
    />
  );
};

export default AdvancedSearchForm;
